import React, { useEffect, useState } from "react";
import Sidebar from "./sidebar";
import axios from "../../utils/axios";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const Aiflag = () => {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user?.id;

  const fetchData = async () => {
    try {
      const res = await axios.get("/inspector/explanation");
      if (res.data.success) {
        setProperties(res.data.explanations);
      }
    } catch (error) {
      console.error("Error fetching properties:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = (property) => {
    setSelectedProperty(property);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedProperty(null);
    setOpen(false);
  };

  const handleFlag = async () => {
    try {
      await axios.post("/properties/flag", {
        propertyId: selectedProperty.property_id,
        userId,
        comment: "Flagged from AI Flag tab"
      });
      toast.success("Property flagged for inspection");
      navigate("/supervisor/flaged");
    } catch (err) {
      toast.error("Failed to flag property");
    }
  };

  const handleAccept = async () => {
    try {
      await axios.post("/properties/accept", {
        propertyId: selectedProperty.property_id,
        userId,
      });
      toast.success("Property Accepted");
      setSelectedProperty(null);
      fetchData();
      setOpen(false);
    } catch (err) {
      console.error("Accept explanation error:", err);
      toast.error("Failed to accept explanation");
    }
  };

  return (
    <div className="flex flex-col h-screen">
      <Sidebar />

      <div className="flex-1 p-6 bg-gray-50 overflow-y-auto">
        <h2 className="text-3xl font-bold mb-6 text-gray-800">
          AI Flag Properties
        </h2>

        {/* Property Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties.map((prop) => (
            <div
              key={prop.explanation_id}
              className="p-5 border border-gray-200 rounded-2xl shadow-md bg-white hover:shadow-lg transition cursor-pointer"
              onClick={() => handleOpenModal(prop)}
            >
              <h3 className="font-semibold text-lg text-gray-800 truncate">
                {prop.pin}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                <span className="font-medium">Circle:</span> {prop.circle}
              </p>
              <p className="text-sm text-gray-600 truncate">
                <span className="font-medium">Location:</span> {prop.location}
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Checked By: {prop.user_name || "N/A"}
              </p>
            </div>
          ))}
        </div>

        {/* Modal */}
        <AnimatePresence>
          {open && selectedProperty && (
            <motion.div
              className="fixed inset-0 flex items-center justify-center bg-black/60 z-50 p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl p-8 overflow-y-auto max-h-[90vh]"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* Close Button */}
                <button
                  onClick={handleCloseModal}
                  className="absolute top-4 right-6 text-gray-500 hover:text-gray-800 text-2xl"
                >
                  ✕
                </button>

                {/* Header */}
                <h2 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-2">
                  Property Details
                </h2>

                {/* General Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-blue-700 mb-2">
                      Basic Information
                    </h3>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li><strong>PIN:</strong> {selectedProperty.pin}</li>
                      <li><strong>Circle:</strong> {selectedProperty.circle}</li>
                      <li><strong>Location:</strong> {selectedProperty.location}</li>
                      <li><strong>Occupation:</strong> {selectedProperty.occupationstatus}</li>
                      <li><strong>Status:</strong> {selectedProperty.status}</li>
                      <li><strong>Checked By:</strong> {selectedProperty.user_name}</li>
                    </ul>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-green-700 mb-2">
                      GARV & Meta
                    </h3>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li><strong>Admin GARV:</strong> {selectedProperty.admin_garv}</li>
                      <li><strong>Expected GARV:</strong> {selectedProperty.exp_garv}</li>
                      <li><strong>Added By:</strong> {selectedProperty.added_by || "N/A"}</li>
                      <li><strong>Updated By:</strong> {selectedProperty.updated_by || "N/A"}</li>
                      <li><strong>Created At:</strong> {new Date(selectedProperty.created_at).toLocaleString()}</li>
                    </ul>
                  </div>
                </div>

                {/* Comparison Table */}
                <h3 className="text-xl font-semibold mb-3 text-gray-800">
                  📊 Admin vs Predicted Values
                </h3>
                <div className="overflow-x-auto mb-8">
                  <table className="min-w-full text-sm border border-gray-200 rounded-lg">
                    <thead className="bg-gray-100 text-gray-700">
                      <tr>
                        <th className="p-2 text-left">Feature</th>
                        <th className="p-2 text-left">Admin</th>
                        <th className="p-2 text-left">Predicted</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="p-2 font-medium">Floor</td>
                        <td className="p-2">{selectedProperty.floor_admin}</td>
                        <td className="p-2">{selectedProperty.floors_pred}</td>
                      </tr>
                      <tr>
                        <td className="p-2 font-medium">Land Use</td>
                        <td className="p-2">{selectedProperty.landuse_admin}</td>
                        <td className="p-2">{selectedProperty.land_use_pred}</td>
                      </tr>
                      <tr>
                        <td className="p-2 font-medium">Land Area</td>
                        <td className="p-2">{selectedProperty.landarea_admin}</td>
                        <td className="p-2">{selectedProperty.landarea_combined_pred}</td>
                      </tr>
                      <tr>
                        <td className="p-2 font-medium">Covered Area</td>
                        <td className="p-2">{selectedProperty.coveredarea_admin}</td>
                        <td className="p-2">{selectedProperty.coveredarea_combined_pred}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Explanations */}
                <h3 className="text-xl font-semibold mb-3 text-gray-800">
                  💬 AI Explanations
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <p><strong>Overall:</strong> {selectedProperty.overall_explanation}</p>
                    <p><strong>Floor:</strong> {selectedProperty.floor_explanation}</p>
                    <p><strong>Land Use:</strong> {selectedProperty.landuse_explanation}</p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <p><strong>Covered Area:</strong> {selectedProperty.coveredarea_explanation}</p>
                    <p><strong>Land Area:</strong> {selectedProperty.landarea_explanation}</p>
                  </div>
                </div>

                {selectedProperty.algorithm_inspector_response && (
                  <>
                    <h3 className="text-xl font-semibold mb-3 text-gray-800">
                      📝 Inspector Response (Flagged by Algorithm)
                    </h3>
                    <div className="bg-green-50 p-4 rounded-lg mb-8 border border-green-200">
                      <p className="text-sm text-green-800 whitespace-pre-wrap">
                        {selectedProperty.algorithm_inspector_response}
                      </p>
                      {selectedProperty.algorithm_response_date && (
                        <p className="text-xs text-green-700 mt-2">
                          Responded on: {new Date(selectedProperty.algorithm_response_date).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end gap-4 mt-6">
                  <button
                    onClick={handleFlag}
                    className="px-6 py-2 bg-red-600 text-white rounded-lg font-medium shadow hover:bg-red-700 focus:ring-2 focus:ring-red-400"
                  >
                    🚩 Flag Property
                  </button>
                  <button
                    onClick={handleAccept}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium shadow hover:bg-green-700 focus:ring-2 focus:ring-green-400"
                  >
                    ✅ Accept
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Aiflag;
